import { useMemo } from "react";

import type { FilterDefinition, FilterOption } from "@/ds";
import { CONDICAO_OPTIONS, PERIOD_COLUMN, STATUS_OPTIONS } from "../financeiro-obras.constants";
import type { FinanceiroQuickOption } from "../types-financeiro-obras";
import { useFinanceiroEmbarcacoes, useFinanceiroTiposObra } from "./use-financeiro-lookups";

/**
 * Colunas de filtro da tela: opções dos lookups do Financeiro (só exigem hub.financeiro:view-painel) e a
 * divisão entre o painel e os filtros rápidos do topo.
 */
export function useFinanceiroFiltrosColunas(isMobile: boolean) {
  const embarcacoes = useFinanceiroEmbarcacoes();
  const tipos = useFinanceiroTiposObra();

  const tipoOptions = useMemo<FinanceiroQuickOption[]>(
    () =>
      tipos.data.map((option) => ({
        label: `${option.codigo} · ${option.nome}`,
        value: option.codigo,
      })),
    [tipos.data],
  );

  /** Todas as colunas de filtro; também dão os rótulos do resumo de filtros da exportação. */
  const allFilterColumns = useMemo<(FilterDefinition & { options: FilterOption[] })[]>(
    () => [
      {
        key: "statusFinanceiro",
        label: "Status",
        options: STATUS_OPTIONS.map(({ value, label }) => ({ value, label })),
        type: "multiselect",
        searchPlaceholder: "Buscar status",
      },
      {
        key: "embarcacaoId",
        label: "Embarcação",
        options: embarcacoes.data.map((option) => ({ value: option.id, label: option.nome })),
        isLoading: embarcacoes.status === "loading",
        hasError: embarcacoes.status === "error" || embarcacoes.status === "forbidden",
        type: "multiselect",
        searchPlaceholder: "Buscar embarcação",
      },
      {
        key: "tipoObra",
        label: "Tipo",
        options: tipoOptions.map(({ value, label }) => ({ value, label })),
        isLoading: tipos.status === "loading",
        hasError: tipos.status === "error" || tipos.status === "forbidden",
        type: "multiselect",
        searchPlaceholder: "Buscar tipo de obra",
      },
      {
        key: "condicao",
        label: "Condição",
        options: CONDICAO_OPTIONS.map(({ value, label }) => ({ value, label })),
        type: "multiselect",
        searchPlaceholder: "Buscar condição",
      },
      { key: PERIOD_COLUMN, label: "Data", options: [], type: "date" },
    ],
    [embarcacoes, tipos.status, tipoOptions],
  );

  /**
   * Colunas do painel: no desktop Status e Tipo ficam nos filtros rápidos do topo (Figma
   * 12760:965620); no mobile eles entram no painel.
   */
  const filterColumns = useMemo<FilterDefinition[]>(() => {
    const byKey = (key: string) => allFilterColumns.filter((column) => column.key === key);

    return isMobile
      ? [
          ...byKey("embarcacaoId"),
          ...byKey("tipoObra"),
          ...byKey("statusFinanceiro"),
          ...byKey("condicao"),
          ...byKey(PERIOD_COLUMN),
        ]
      : [...byKey("embarcacaoId"), ...byKey("condicao"), ...byKey(PERIOD_COLUMN)];
  }, [allFilterColumns, isMobile]);

  return {
    allFilterColumns,
    filterColumns,
    tipoOptions,
    isTipoDisabled: tipos.status !== "success",
  };
}
